import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut } from "lucide-react";
import { LangProvider, useLang } from "../lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "terrain_admin_token";

function LoginForm({ onLogin }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onLogin(r.data.token);
    } catch {
      setError(t.admin.loginError);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-edge bg-surface px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-signal focus:outline-none transition-colors duration-200";

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-edge bg-void p-10" data-testid="admin-login-form">
        <p className="font-display text-lg font-semibold tracking-tight">
          TERRAIN<span className="text-signal">®</span>
        </p>
        <h1 className="mb-8 mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
          {t.admin.loginTitle}
        </h1>
        <div className="space-y-4">
          <input
            className={inputCls}
            type="email"
            placeholder={t.admin.email}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
          />
          <input
            className={inputCls}
            type="password"
            placeholder={t.admin.password}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
          />
        </div>
        {error && (
          <p className="mt-4 font-mono text-xs text-signal" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 flex w-full items-center justify-center gap-2 bg-signal py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-signalhover disabled:opacity-40"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {t.admin.login}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = () =>
    axios
      .get(`${API}/bookings`, { headers })
      .then((r) => setBookings(r.data.bookings))
      .catch((e) => {
        if (e.response?.status === 401) onLogout();
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/bookings/${id}`, { status }, { headers });
    setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  return (
    <div className="min-h-screen px-6 py-10 md:px-10" data-testid="admin-dashboard">
      <div className="mb-10 flex items-center justify-between border-b border-edge pb-6">
        <div>
          <p className="font-display text-lg font-semibold tracking-tight">
            TERRAIN<span className="text-signal">®</span>
          </p>
          <h1 className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
            {t.admin.title}
          </h1>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 border border-edge px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors duration-200 hover:border-signal hover:text-foreground"
        >
          <LogOut className="h-3.5 w-3.5" />
          {t.admin.logout}
        </button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-signal" />
        </div>
      ) : bookings.length === 0 ? (
        <p className="font-mono text-sm text-muted-foreground" data-testid="admin-empty">
          {t.admin.empty}
        </p>
      ) : (
        <div className="overflow-x-auto border border-edge">
          <table className="w-full text-left text-sm" data-testid="admin-bookings-table">
            <thead>
              <tr className="border-b border-edge font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <th className="px-4 py-3">{t.admin.date}</th>
                <th className="px-4 py-3">{t.admin.slot}</th>
                <th className="px-4 py-3">{t.admin.name}</th>
                <th className="px-4 py-3">{t.admin.company}</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">{t.admin.message}</th>
                <th className="px-4 py-3">{t.admin.status}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-edge/50 transition-colors duration-150 hover:bg-surface" data-testid={`admin-booking-row-${b.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{b.date}</td>
                  <td className="px-4 py-3 font-mono text-xs">{b.slot}</td>
                  <td className="px-4 py-3">{b.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{b.company || "—"}</td>
                  <td className="px-4 py-3">
                    <a href={`mailto:${b.email}`} className="text-signal hover:underline">
                      {b.email}
                    </a>
                  </td>
                  <td className="max-w-56 truncate px-4 py-3 text-muted-foreground" title={b.message}>
                    {b.message || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={b.status}
                      onChange={(e) => setStatus(b.id, e.target.value)}
                      data-testid={`admin-status-select-${b.id}`}
                      className="border border-edge bg-void px-2 py-1 font-mono text-[11px] uppercase text-foreground focus:border-signal focus:outline-none"
                    >
                      {["new", "confirmed", "done", "cancelled"].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminInner() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setChecking(false));
  }, [token]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-signal" />
      </div>
    );
  }
  if (!token) return <LoginForm onLogin={setToken} />;
  return (
    <Dashboard
      token={token}
      onLogout={() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }}
    />
  );
}

export default function AdminPage() {
  return (
    <main className="min-h-screen bg-void text-foreground" data-testid="admin-page">
      <LangProvider>
        <AdminInner />
      </LangProvider>
    </main>
  );
}
